import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import FlagChart from './charts/flag-chart';
import MainChart from './charts/main-chart';
import CrosshairTooltip from './crosshair-tooltip';
import Card from '@components-clean/core/card';
import { InfoField } from '@components-clean/core/info-field';
import { useDebouncedValue, useRefArray, useInterval } from '@hooks-clean/index';
import { TimelineObjDto, SubjectDetailsModel } from '@interfaces-clean/subject-monitoring.interface';
import StandaloneNavigator from '@pages-clean/subject-monitoring/subject-profile/tabs/timeline/charts/navigator-chart';
import StandaloneXAxis from '@pages-clean/subject-monitoring/subject-profile/tabs/timeline/charts/x-axis-chart';
import { usePersistentStartingExtremes } from '@pages-clean/subject-monitoring/subject-profile/tabs/timeline/hooks/use-persistent-starting-extremes';
import {
    CHART_COLORS,
    CrosshairPosition,
    TimelineHighchartsReactRefObject,
} from '@pages-clean/subject-monitoring/subject-profile/tabs/timeline/shared';
import { useLocalState, useTranslation } from '@hooks/index';
import { javascriptDateWithoutTimeFromString } from '@utils/convertor';
import { dateToTimestamp } from '@utils/date-helpers';
import { getPanels_OpraQueries_panels_Panel } from '@queries/generated/central-monitoring.generated';
// eslint-disable-next-line max-len
import { subjectProfileDetailsConfiguration_OpraQueries_subjectProfileDetailsConfiguration_SubjectProfileDetailsConfiguration_subjectProfileDetailsConfigurationItem_SubjectProfileDetailsConfigurationItem as SubjectDetailsConfig } from '@queries/generated/subject-monitoring.generated';
import './styles.scss';

interface Props {
    config: getPanels_OpraQueries_panels_Panel[];
    data: TimelineObjDto;
    datasetCutDate: Date;
    datasetId: number;
    subjectId: number;
    subjectDetails: SubjectDetailsModel;
    subjectDetailsConfig: SubjectDetailsConfig;
}

const SubjectProfileTimeline = ({
    config,
    data,
    datasetCutDate,
    datasetId,
    subjectId,
    subjectDetails,
    subjectDetailsConfig,
}: Props) => {
    const { state } = useLocalState();
    const { t } = useTranslation('admin');
    const { t: tSm } = useTranslation('sm', {
        keyPrefix: 'subjectProfile',
    });
    // Track how many synchronizations of charts (via interval) have been done
    const [synchronizeIntervalsDone, setSynchronizeIntervalsDone] = useState(0);

    // Track x position of shared crosshair
    const [crosshairPosition, setCrosshairPosition] = useState<CrosshairPosition>();

    const startDate = subjectDetailsConfig.timeline?.startDate;
    const endDateFields = subjectDetailsConfig.timeline?.endDateFields;
    const sortAscending = subjectDetailsConfig.timeline?.sortAscending;

    const chartRefs = useRefArray<TimelineHighchartsReactRefObject | null>({
        size: config.length + 4,
        initialValue: null,
    });

    const endDate = useMemo(() => {
        if (endDateFields == null) {
            return null;
        }

        const getEndDateFnMap = new Map<string, (subjectDetails: SubjectDetailsModel) => string | null | undefined>([
            ['Subject.fuCompletedDate', (s) => s.fuCompletedDate],
            ['Subject.screeningDate', (s) => s.screeningDate],
            ['Subject.screeningFailDate', (s) => s.screeningFailDate],
            ['Subject.txDiscontinuedDate', (s) => s.txDiscontinuedDate],
            ['Subject.studyDiscontinuedDate', (s) => s.studyDiscontinuedDate],
            ['Subject.enrolledDate', (s) => s.enrolledDate],
            ['Subject.randomizedDate', (s) => s.randomizedDate],
            ['Subject.fuStartDate', (s) => s.fuStartDate],
            ['Subject.startOfTxDate', (s) => s.txStartDate],
            ['Subject.completedTxDate', (s) => s.txCompletedDate],
            ['Subject.completedStudyDate', (s) => s.completedStudyDate],
            ['Subject.lostToFUDate', (s) => s.lostToFUDate],
        ]);

        const selectedDates = endDateFields
            // Map field keys to SubjectDetails values (or null, if no mapping exists)
            .map((k) => (getEndDateFnMap.has(k) ? getEndDateFnMap.get(k)!(subjectDetails) : null))
            // Remove nulls (no SubjectDetails value for field key, or no mapping)
            .filter((x) => x != null)
            .map((s) => new Date(s));

        // If there are no defined date values for the field keys, use current date
        if (selectedDates.length === 0) {
            return dateToTimestamp(new Date());
        }

        if (sortAscending) {
            selectedDates.sort((a, z) => a.getTime() - z.getTime());
        } else {
            selectedDates.sort((a, z) => z.getTime() - a.getTime());
        }

        return dateToTimestamp(selectedDates[0]);
    }, [subjectDetails, endDateFields, sortAscending]);

    const { startingExtremes, extremesRef, setExtremes } = usePersistentStartingExtremes({
        datasetId,
        subjectId,
        startDate,
        endDate,
        datasetCutDate,
    });

    interface IChartDetail {
        marginLeft?: number;
        marginRight?: number;
    }

    interface IPositionDetail {
        largestMarginLeft: number;
        largestMarginRight: number;
        mainCharts: IChartDetail[];
    }

    const [positionDetails, setPositionDetails] = useState<IPositionDetail>();
    // The charts animate resizing when the `positionDetails` are set.
    // So we wait some time after `positionDetails` is set before revealing the charts...
    const hasFinishedLoading = useDebouncedValue(positionDetails, 150) != null;

    // Calculates the margin left/right that each chart needs so that their plot areas are all the same width and aligned
    const synchronizeChartSizes = useCallback(() => {
        const getChartMarginRight = (c: Highcharts.Chart) => c.chartWidth - (c.plotLeft + c.plotWidth);

        const charts = chartRefs.current
            .filter((r) => r != null) // Get defined charts
            .map((r) => r.chart);

        charts.forEach((c) => c.redraw());

        const largestMarginLeft = Math.max(0, ...charts.map((c) => c.plotLeft));
        const largestMarginRight = Math.max(0, ...charts.map((c) => getChartMarginRight(c)));

        // Work out how much margin needs to be added around each chart so that their plot areas are all the same width
        const mainCharts = charts
            .slice(1, -1) // Skip 1st chart (navigator) and last chart (x-axis)
            .map<IChartDetail>((c) => ({
                // Shouldn't be possible to be less than 0, but bound it just in case...
                marginLeft: Math.max(0, largestMarginLeft - c.plotLeft),
                marginRight: Math.max(0, largestMarginRight - getChartMarginRight(c)),
            }));

        setPositionDetails({
            // For positioning navigator / x-axis / main chart titles
            largestMarginLeft,
            largestMarginRight,
            // For positioning individual main charts
            mainCharts,
        });
    }, [chartRefs]);

    const synchronizeChartExtremes = useCallback(() => {
        const extremes = extremesRef.current ?? startingExtremes;
        if (extremes == null) {
            return;
        }

        const { min, max } = extremes;

        chartRefs.current
            .filter((r) => r != null)
            .flatMap((r) => r.chart.axes)
            .filter((a) => a.isXAxis)
            .forEach((a) => {
                a.setExtremes(min, max);
            });
    }, [chartRefs, extremesRef, startingExtremes]);

    // On mount, and when the `config` (the panels to display) changes, synchronize chart sizes and extremes
    useEffect(() => {
        synchronizeChartSizes();
        synchronizeChartExtremes();
    }, [synchronizeChartSizes, synchronizeChartExtremes, config]);

    // Then, for 5 200ms intervals, synchronize charts sizes and extremes (which may or may not actually do anything)
    // This was originally added for the popout view, which has different page wrapper elements which caused layout issues.
    // It may or may not still be needed...
    useInterval(
        () => {
            synchronizeChartSizes();
            synchronizeChartExtremes();

            setSynchronizeIntervalsDone((x) => x + 1);
        },
        synchronizeIntervalsDone < 5 ? 200 : null,
    );

    const synchronizeTooltips = useCallback(
        (point: Highcharts.Point | undefined) => {
            const charts = chartRefs.current
                .filter((r) => r != null) // Get defined charts
                .map((r) => r.chart);

            // Clear old tooltips
            charts.forEach((c) => {
                c.tooltip.hide();
                c.series.forEach((s) => {
                    s.setState('normal');
                    s.points.forEach((p) => p.setState('normal'));
                });
            });

            if (point == null) {
                return;
            }

            charts.forEach((c) => {
                // Get points on chart (any single-value series) which share x-value with hovered point
                const matchedPoints = c.series
                    .filter((s) => s.type !== 'xrange')
                    .flatMap((s) => s.points)
                    .filter((p) => p.x === point.x);

                matchedPoints.forEach((p) => p.setState('hover'));

                if (matchedPoints.length > 0) {
                    c.tooltip.refresh(matchedPoints);
                } else {
                    c.tooltip.hide();
                }
            });
        },
        [chartRefs],
    );

    const synchronizeCrosshairs = useCallback(
        (pos: CrosshairPosition | undefined) => {
            setCrosshairPosition((old) => {
                // Don't create a new object (and cause a rerender) if the property values haven't actually changed
                if (old?.plotX !== pos?.plotX || old?.value !== pos?.value) {
                    return pos;
                }
                return old;
            });

            const charts = chartRefs.current
                .filter((r) => r != null) // Get defined charts
                .map((r) => r.chart);

            // Remove previous crosshair
            charts.forEach((c) => {
                c.customCrosshairElement?.destroy();
            });

            if (pos == null) {
                return;
            }

            // Draw crosshair for each chart
            charts.forEach((c) => {
                c.customCrosshairElement = c.renderer
                    .rect(pos.plotX + c.plotLeft - 1, c.plotTop, 2, c.plotHeight)
                    .attr({
                        fill: '#888888',
                        zIndex: 2,
                    })
                    .add();
            });
        },
        [chartRefs, setCrosshairPosition],
    );

    const basisDate = useMemo(
        () =>
            // Calendar date only (same as milestones/tags): take the date part as UTC midnight, so the "Day N"
            // counts are calendar-day differences. The serialized time is ambiguous and would otherwise shift the count.
            subjectDetails.timelineBasisDate
                ? (javascriptDateWithoutTimeFromString(subjectDetails.timelineBasisDate) ?? undefined)
                : undefined,
        [subjectDetails.timelineBasisDate],
    );

    const milestones = useMemo(() => {
        if (!endDate) return [];

        return data.milestones
            .map((milestone) => {
                const fieldLabel =
                    state?.cm2Fields?.filter((f) => f.key?.toLocaleLowerCase() === milestone.fieldKey)[0]?.name ??
                    milestone.fieldKey;

                return {
                    date: new Date(milestone.date.split('T')[0]),
                    label: fieldLabel ?? '',
                };
            })
            .filter(
                (m) =>
                    m.date.getTime() >= new Date(startDate).getTime() &&
                    m.date.getTime() <= new Date(endDate).getTime(),
            )
            .toSorted((a, z) => a.date.getTime() - z.date.getTime());
    }, [data.milestones, startDate, endDate, state?.cm2Fields]);

    const tags = useMemo(() => {
        if (!endDate) return [];

        return (
            data.tags
                // See the milestone note: take the calendar date as UTC midnight; the chart centres it at midday.
                .map((tag) => ({ date: new Date(tag.date.split('T')[0]), label: tag.label }))
                .filter(
                    (m) =>
                        m.date.getTime() >= new Date(startDate).getTime() &&
                        m.date.getTime() <= new Date(endDate).getTime(),
                )
                .toSorted((a, z) => a.date.getTime() - z.date.getTime())
        );
    }, [data.tags, startDate, endDate]);

    if (startDate == null || endDate == null || sortAscending == null) {
        return (
            <div className="loading-and-message-container">
                <InfoField type="Info" message={tSm('timeline.timelineNotConfigured')} />
            </div>
        );
    }

    if (new Date(endDate) < new Date(startDate)) {
        return (
            <div className="loading-and-message-container">
                <InfoField type="Error" message={tSm('timeline.startDateAfterEndDate')} />
            </div>
        );
    }

    // Shouldn't be reachable due to other checks, but it makes TS infer that the types are defined in the JSX
    if (startingExtremes == null) {
        return (
            <div className="loading-and-message-container">
                <InfoField type="Error" message={t('common:somethingWentWrong')} />
            </div>
        );
    }

    const navigatorAndXAxisOptions = {
        xAxis: {
            type: 'datetime',
            min: startDate,
            max: endDate,
            crosshair: true,
            ordinal: false,
            visible: false,
            minRange: 2 * 24 * 3600 * 1000, // 2 days in milliseconds
        } satisfies Highcharts.XAxisOptions,
        series: [
            {
                type: 'xrange',
                name: 'Timeline',
                pointWidth: 20,
                data: [],
            },
        ] satisfies Highcharts.SeriesXrangeOptions[],
    };

    return (
        <Card
            data-testid="timeline"
            className="subject-profile-timeline"
            bodyClassName="subject-profile-timeline-inner"
        >
            {/* `data-loaded` determines whether element has 0% opacity (false) or 100% opacity (true) */}
            {/* It's to hide the ugly (seemingly un-disableable) resize animation that the charts do when resized */}
            <div className="chart-container" data-loaded={hasFinishedLoading}>
                <div className="navigator">
                    <div
                        style={{
                            marginLeft: positionDetails != null ? positionDetails.largestMarginLeft - 9 : undefined,
                            marginRight: positionDetails != null ? positionDetails.largestMarginRight - 11 : undefined,
                        }}
                    >
                        <StandaloneNavigator
                            ref={(el) => {
                                chartRefs.current[0] = el;
                            }}
                            chartRefs={chartRefs.current}
                            options={navigatorAndXAxisOptions}
                            borderColor={CHART_COLORS.BORDER_COLOR}
                            startingExtremes={startingExtremes}
                            onExtremesChanged={setExtremes}
                        />
                    </div>
                </div>

                {config.map((panel, panelIndex) => {
                    const isEven = panelIndex % 2 === 0;
                    const panelData = data.panelResults[panel.id!];

                    return (
                        <div
                            key={panel.id}
                            className="chart content"
                            style={{
                                marginLeft: positionDetails?.mainCharts[panelIndex]?.marginLeft,
                                marginRight: positionDetails?.mainCharts[panelIndex]?.marginRight,
                            }}
                        >
                            <MainChart
                                startDate={new Date(startDate).getTime()}
                                endDate={new Date(endDate).getTime()}
                                config={panel}
                                title={panel.name ?? ''}
                                data={panelData}
                                ref={(el) => {
                                    chartRefs.current[panelIndex + 1] = el;
                                }}
                                backgroundColor={isEven ? CHART_COLORS.BACKGROUND_ALT : CHART_COLORS.BACKGROUND}
                                borderColor={CHART_COLORS.BORDER_COLOR}
                                startingExtremes={startingExtremes}
                                basisDate={basisDate}
                                synchronizeTooltips={synchronizeTooltips}
                                synchronizeCrosshairs={synchronizeCrosshairs}
                            />
                        </div>
                    );
                })}

                <div
                    className="chart content"
                    style={{
                        marginLeft: positionDetails?.mainCharts[config.length]?.marginLeft,
                        marginRight: positionDetails?.mainCharts[config.length]?.marginRight,
                    }}
                >
                    <FlagChart
                        type="Tag"
                        ref={(el) => {
                            chartRefs.current[config.length + 1] = el;
                        }}
                        chartRefs={chartRefs.current}
                        startDate={new Date(startDate).getTime()}
                        endDate={new Date(endDate).getTime()}
                        startingExtremes={startingExtremes}
                        data={tags}
                        synchronizeCrosshairs={synchronizeCrosshairs}
                    />
                </div>

                <div
                    className="chart content"
                    style={{
                        marginLeft: positionDetails?.mainCharts[config.length + 1]?.marginLeft,
                        marginRight: positionDetails?.mainCharts[config.length + 1]?.marginRight,
                    }}
                >
                    <FlagChart
                        type="Milestone"
                        ref={(el) => {
                            chartRefs.current[config.length + 2] = el;
                        }}
                        chartRefs={chartRefs.current}
                        startDate={new Date(startDate).getTime()}
                        endDate={new Date(endDate).getTime()}
                        startingExtremes={startingExtremes}
                        data={milestones}
                        synchronizeCrosshairs={synchronizeCrosshairs}
                    />
                </div>

                <div className="x-axis">
                    <div
                        style={{
                            marginLeft: positionDetails != null ? positionDetails.largestMarginLeft - 10 : undefined,
                            marginRight: positionDetails != null ? positionDetails.largestMarginRight - 10 : undefined,
                            position: 'relative',
                        }}
                    >
                        <StandaloneXAxis
                            ref={(el) => {
                                chartRefs.current[config.length + 3] = el;
                            }}
                            options={navigatorAndXAxisOptions}
                            basisDate={basisDate}
                            borderColor={CHART_COLORS.BORDER_COLOR}
                            labelColor={CHART_COLORS.COLOUR_PRIMARY}
                            startingExtremes={startingExtremes}
                        />

                        <CrosshairTooltip crosshairPosition={crosshairPosition} basisDate={basisDate} />
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default SubjectProfileTimeline;
